import { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS, EMPTY_STRING } from '../../constants';
import { useGlobalSearchStore, useRouteStore } from '../../Store';
import GlobalSearchRemote from "../GlobalSearchRemote/GlobalSearchRemote";
import { useUserProfile, useAllWorkflowsAndApplications } from '../../hooks/queries';
import { usePaneManagement } from '../../contexts/PaneManagementContext';
import { setDocumentTitle } from '../../util/TitleManager';
import { ClickCallbackResponse } from 'global-search/dist/src/components/Distributed/types';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/Theme';
import launchExternal from '../../util/launchExternal';
import { ErrorBoundary } from '../../util/ErrorBoundary';

export default function GlobalSearchResult() {

    const { data: userInfo } = useUserProfile();
    const { oktaAuth } = useOktaAuth();
    const [accessToken, setAccessToken] = useState('');
    const oktaToken = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}').accessToken;
    const searchQuery = useGlobalSearchStore((state: any) => state.searchQuery);
    const setWasPaneOpened = useGlobalSearchStore((state: any) => state.setWasPaneOpened);
    const wasPaneOpened = useGlobalSearchStore((state: any) => state.wasPaneOpened);
    const setSearchResultItemSelected =  useGlobalSearchStore((state: any) => state.setSearchResultItemSelected);
    const { trackEvent } = useAnalytics();
    const { i18n } = useTranslation();
    const { darkMode } = useTheme();

    useEffect(() => {
        setAccessToken(oktaToken?.accessToken);
        setUserName(userInfo?.userProfile?.login || '');
        setStoreId(userInfo?.userProfile?.defaultStore || "");
    }, [oktaToken, userInfo?.userProfile?.login, userInfo?.userProfile?.defaultStore]);

    useEffect(() => {
        const handleRenewed = (key: string, newToken: any) => {
            if (key === 'accessToken' && newToken?.accessToken) {
                setAccessToken(newToken.accessToken);
            }
        };
        oktaAuth?.tokenManager?.on('renewed', handleRenewed);
        return () => {
            oktaAuth?.tokenManager?.off('renewed', handleRenewed);
        };
    }, [oktaAuth]);

    const [userName, setUserName] = useState('');
    const [storeId, setStoreId] = useState('');
    const { data } = useAllWorkflowsAndApplications();
    const previousRoute = useRouteStore((state: any) => state.previousRoute);
    const setRoute = useRouteStore((state: any) => state.setRoute);
    const setPreviousRoute = useRouteStore((state: any) => state.setPreviousRoute);
    const { openCtaPane, activePane, isPaneExpaneded, toggleIsPaneExpanded, setActivePane, openPanes } = usePaneManagement();
    const setResultItemSelectedFromResultPage = useGlobalSearchStore((state: any) => state.setResultItemSelectedFromResultPage);

    const workflowNames = data.map((app: any) => {
        const workflow = app.appId
            ? {
                ...app,
                logoutUrl:
                    data.find((item) => item.id === app.appId)?.logoutUrl || '',
            }
            : { ...app };
        return workflow;
    });

    const handleBackButton = () => {
        setActivePane(null);
        if (openPanes.length > 0) {
            setActivePane(openPanes[openPanes.length - 1])
        }

        if (wasPaneOpened) {
            const app = (workflowNames || data).find((workflow: any) => workflow.id === activePane);
            isPaneExpaneded ? setDocumentTitle(EMPTY_STRING, EMPTY_STRING) : setDocumentTitle(activePane ? app?.name : EMPTY_STRING, EMPTY_STRING);
            setWasPaneOpened(false);
            toggleIsPaneExpanded(true);
        }
        if (previousRoute === '') {
            setRoute('/applications');
        }
        else {
            setRoute(previousRoute);
        }
        setPreviousRoute('');
        trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, { 'Item': 'Global Search Back Button' });
    };

    const openTileInNewTab = (id: string, url: string) => {
        const dpId = (id.substring(0, 1) === 'F' && id !== 'FORTELLIS') ? `dp_wf_${id}` : `dp_ap_${id}`;
        window.open(`${window.location.protocol}//${window.location.host}/${dpId}/${url}?GS=true`, '_blank');
    };

    const openGlobalSearchItem = (response: ClickCallbackResponse) => {
        try {
            if (response.newTab) {
                openTileInNewTab(response.id, response.url);
             }
            else {
                setSearchResultItemSelected(true);
                setResultItemSelectedFromResultPage(true);
                launchExternal(response.url) || openCtaPane(response.url, response.id);
            }
        } catch {
        }
    };

    return (
        <div >
            <ErrorBoundary>
            <GlobalSearchRemote
                variant="results"
                accessToken={accessToken}
                query={searchQuery}
                storeId={storeId}
                userName={userName}
                onClickCallback={openGlobalSearchItem}
                onBackButtonClicked={handleBackButton}
                lng={i18n.language}
                darkMode={!!darkMode}/></ErrorBoundary></div>
    );
}
